import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, Info, MapPin, Clock, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  productPrices, 
  productCategories, 
  termsAndConditions, 
  formatPrice, 
  searchProducts,
  ProductPrice
} from '@/data/productPrices';

const PriceList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Filtrar productos
  const filteredProducts = useMemo(() => {
    let products = productPrices;
    
    // Filtrar por búsqueda
    if (searchQuery) {
      products = searchProducts(searchQuery);
    }
    
    // Filtrar por categoría
    if (selectedCategory !== 'all') {
      products = products.filter(product => product.category === selectedCategory);
    }
    
    return products;
  }, [searchQuery, selectedCategory]);

  // Obtener badge de disponibilidad
  const getAvailabilityBadge = (availability: ProductPrice['availability']) => {
    const badges = {
      'disponible': { text: 'Disponible', className: 'bg-seagreen-100 text-seagreen-700 border-seagreen-200' },
      'estacional': { text: 'Estacional', className: 'bg-coral-100 text-coral-700 border-coral-200' },
      'agotado': { text: 'Agotado', className: 'bg-silver-200 text-silver-700 border-silver-300' }
    };
    
    const badge = badges[availability];
    return (
      <Badge className={`${badge.className} border font-medium text-xs px-3 py-1.5 whitespace-nowrap`}>
        {badge.text}
      </Badge>
    );
  };

  // Obtener badge de tipo
  const getTypeBadge = (type: ProductPrice['type']) => {
    const badges = {
      'vivo': { text: 'Vivo', className: 'bg-jade-100 text-jade-800 border-jade-200', icon: '🌊' },
      'fresco': { text: 'Fresco', className: 'bg-ocean-100 text-ocean-800 border-ocean-200', icon: '❄️' },
      'congelado': { text: 'Congelado', className: 'bg-pearl-200 text-deep-700 border-pearl-300', icon: '🧊' },
      'precocido': { text: 'Precocido', className: 'bg-oyster-200 text-deep-700 border-oyster-300', icon: '🍳' }
    };
    
    const badge = badges[type];
    return (
      <Badge variant="outline" className={`${badge.className} border font-medium text-xs px-3 py-1.5 whitespace-nowrap`}>
        <span className="mr-1">{badge.icon}</span>
        {badge.text}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen relative bg-gradient-to-br from-pearl-50/50 via-white to-ocean-25/30">
      {/* Background image with overlay */}
      <div 
        className="absolute inset-0 opacity-20 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: "url('/images/hero.jpg')",
          backgroundBlendMode: 'soft-light'
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-pearl-50/70 via-white/80 to-ocean-25/50" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        
        {/* Info Premium Section - Mobile First */}
        <motion.div 
          className="mb-8 sm:mb-12"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          {/* Cards informativos */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8">
            <motion.div
              className="bg-gradient-to-br from-jade-50 to-seagreen-50 p-4 sm:p-6 rounded-2xl border border-jade-200/50 shadow-sm"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-jade-500 rounded-xl flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-jade-800 text-lg">Origen</h3>
              </div>
              <p className="text-jade-700 text-sm sm:text-base">Patagonia Argentina - Aguas prístinas del sur</p>
            </motion.div>

            <motion.div
              className="bg-gradient-to-br from-ocean-50 to-blue-50 p-4 sm:p-6 rounded-2xl border border-ocean-200/50 shadow-sm"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-ocean-500 rounded-xl flex items-center justify-center">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-ocean-800 text-lg">Frescura</h3>
              </div>
              <p className="text-ocean-700 text-sm sm:text-base">Entrega en 24-48h - Productos del día</p>
            </motion.div>

            <motion.div
              className="bg-gradient-to-br from-coral-50 to-orange-50 p-4 sm:p-6 rounded-2xl border border-coral-200/50 shadow-sm sm:col-span-2 lg:col-span-1"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-coral-500 rounded-xl flex items-center justify-center">
                  <Star className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-coral-800 text-lg">Calidad</h3>
              </div>
              <p className="text-coral-700 text-sm sm:text-base">Productos premium certificados</p>
            </motion.div>
          </div>

          {/* Aviso importante */}
          <motion.div
            className="bg-gradient-to-r from-jade-500/10 via-jade-400/5 to-ocean-500/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-jade-200/30 shadow-sm"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              <div className="flex-shrink-0">
                <motion.div 
                  className="w-12 h-12 bg-gradient-to-br from-jade-500 to-ocean-500 rounded-2xl flex items-center justify-center"
                  animate={{ 
                    scale: [1, 1.1, 1],
                    opacity: [0.8, 1, 0.8]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <motion.div
                    animate={{
                      rotate: [0, 5, -5, 0]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <Info className="w-6 h-6 text-white" />
                  </motion.div>
                </motion.div>
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-deep-700 text-lg mb-2">Información Importante</h3>
                <p className="text-deep-600 text-sm sm:text-base leading-relaxed">
                  Los precios son orientativos y pueden variar según disponibilidad y temporada. 
                  <span className="font-medium text-jade-700"> Consultá disponibilidad y precios actualizados.</span>
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Búsqueda y Filtros - Mobile First */}
        <motion.div 
          className="mb-8 sm:mb-12"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-white/40 p-4 sm:p-6">
            
            {/* Barra de búsqueda */}
            <div className="relative mb-4 sm:mb-6">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-silver-400" />
              </div>
              <Input
                type="text"
                placeholder="Buscar productos... (ej: ostras, mejillones, langostinos)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-3 sm:py-4 text-base rounded-xl border-silver-300/50 focus:border-jade-400 focus:ring-jade-400/20 bg-white/90 backdrop-blur-sm shadow-sm transition-all duration-300 placeholder:text-silver-400"
              />
            </div>

            {/* Filtros de categoría - Mobile Scrollable */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-deep-600 mb-3 sm:mb-0 sm:mr-4 sm:inline-flex sm:items-center">
                  <Filter className="w-4 h-4 mr-2" />
                  Categorías:
                </h4>
                
                {/* Mobile: Horizontal scroll, Desktop: Flex wrap */}
                <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 sm:overflow-visible sm:flex-wrap">
                  <Button
                    variant={selectedCategory === 'all' ? 'default' : 'outline'}
                    onClick={() => setSelectedCategory('all')}
                    className={`flex-shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition-all duration-300 ${
                      selectedCategory === 'all'
                        ? 'bg-gradient-to-r from-jade-500 to-ocean-500 text-white shadow-lg hover:shadow-xl'
                        : 'bg-white/80 text-deep-600 border-silver-300/50 hover:bg-jade-50 hover:border-jade-300'
                    }`}
                  >
                    Todos
                    <Badge variant="secondary" className="ml-2 bg-white/90 text-deep-600 text-xs">
                      {productPrices.length}
                    </Badge>
                  </Button>
                  
                  {Object.entries(productCategories).map(([categoryId, category]) => {
                    const categoryCount = productPrices.filter(p => p.category === categoryId).length;
                    return (
                      <Button
                        key={categoryId}
                        variant={selectedCategory === categoryId ? 'default' : 'outline'}
                        onClick={() => setSelectedCategory(categoryId)}
                        className={`flex-shrink-0 rounded-xl px-4 py-2 text-sm font-medium transition-all duration-300 ${
                          selectedCategory === categoryId
                            ? 'bg-gradient-to-r from-jade-500 to-ocean-500 text-white shadow-lg hover:shadow-xl'
                            : 'bg-white/80 text-deep-600 border-silver-300/50 hover:bg-jade-50 hover:border-jade-300'
                        }`}
                      >
                        <span className="mr-2">{category.icon}</span>
                        {category.name}
                        <Badge variant="secondary" className="ml-2 bg-white/90 text-deep-600 text-xs">
                          {categoryCount}
                        </Badge>
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Resultados counter */}
            {(searchQuery || selectedCategory !== 'all') && (
              <motion.div
                className="mt-4 pt-4 border-t border-silver-200/50"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.3 }}
              >
                <p className="text-sm text-deep-600">
                  <span className="font-semibold text-jade-600">{filteredProducts.length}</span> 
                  {filteredProducts.length === 1 ? ' producto encontrado' : ' productos encontrados'}
                  {searchQuery && (
                    <span> para "<span className="font-medium text-ocean-600">{searchQuery}</span>"</span>
                  )}
                </p>
              </motion.div>
            )}

            {/* Términos y condiciones compactos */}
            <motion.div
              className="mt-6 bg-gradient-to-r from-jade-50/50 to-ocean-50/50 backdrop-blur-sm rounded-xl p-4 border border-jade-200/30"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <motion.div 
                    className="w-8 h-8 bg-gradient-to-br from-jade-500 to-ocean-500 rounded-lg flex items-center justify-center"
                    animate={{ 
                      scale: [1, 1.1, 1],
                      opacity: [0.8, 1, 0.8]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <motion.div
                      animate={{
                        rotate: [0, 5, -5, 0]
                      }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    >
                      <Info className="w-4 h-4 text-white" />
                    </motion.div>
                  </motion.div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-jade-800 text-sm mb-2">Condiciones Importantes</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-jade-700">
                    {termsAndConditions.notes.slice(0, 4).map((note, index) => (
                      <p key={index} className="flex items-start">
                        <span className="text-jade-500 mr-1">•</span>
                        <span className="flex-1">{note}</span>
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Lista de productos - Mobile First Grid */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          {filteredProducts.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              whileHover={{ y: -4 }}
            >
              <Card className="group h-full bg-white/90 backdrop-blur-sm border border-white/60 shadow-lg hover:shadow-2xl transition-all duration-300 rounded-2xl overflow-hidden">
                
                {/* Header con badges */}
                <CardHeader className="pb-3 relative">
                  {/* Badge de disponibilidad en esquina superior */}
                  <div className="absolute -top-2 -right-2 z-10">
                    {getAvailabilityBadge(product.availability)}
                  </div>
                  
                  {/* Título del producto */}
                  <CardTitle className="font-bold text-lg sm:text-xl text-deep-700 group-hover:text-ocean-600 transition-colors duration-300 leading-tight pr-16 mb-1">
                    {product.name}
                  </CardTitle>
                  
                  {/* Badges de tipo y origen */}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {getTypeBadge(product.type)}
                    <Badge variant="outline" className="bg-pearl-50/80 text-deep-600 border-pearl-200 text-xs px-3 py-1.5 whitespace-nowrap">
                      <MapPin className="w-3 h-3 mr-1" />
                      {product.origin}
                    </Badge>
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  {/* Precio destacado */}
                  <div className="bg-gradient-to-br from-jade-50/50 to-ocean-50/50 rounded-xl p-4 mb-4 border border-jade-200/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-lg sm:text-xl font-bold text-transparent bg-gradient-to-r from-jade-600 to-ocean-600 bg-clip-text">
                          {formatPrice(product.price)}
                        </div>
                        <div className="text-sm text-deep-600 font-medium">
                          por {product.unit}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="w-12 h-12 bg-gradient-to-br from-jade-500 to-ocean-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-lg">
                            {productCategories[product.category as keyof typeof productCategories]?.icon || '🐚'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Descripción */}
                  {product.description && (
                    <p className="text-sm text-deep-600 mb-3 leading-relaxed">
                      {product.description}
                    </p>
                  )}

                  {/* Pedido mínimo */}
                  {product.minimumOrder && (
                    <div className="bg-coral-50/50 rounded-lg p-3 border border-coral-200/30">
                      <p className="text-xs text-coral-700 font-medium flex items-center">
                        <Info className="w-3 h-3 mr-1 flex-shrink-0" />
                        Pedido mínimo: {product.minimumOrder}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Sin resultados */}
        {filteredProducts.length === 0 && (
          <motion.div
            className="text-center py-12 sm:py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <div className="text-6xl sm:text-8xl mb-4">🔍</div>
            <h3 className="font-semibold text-xl sm:text-2xl text-deep-600 mb-2">
              No se encontraron productos
            </h3>
            <p className="text-deep-500 text-sm sm:text-base mb-6 max-w-md mx-auto">
              Intenta cambiar los filtros o ajustar tu búsqueda
            </p>
            <Button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
              }}
              className="bg-gradient-to-r from-jade-500 to-ocean-500 hover:from-jade-600 hover:to-ocean-600 text-white rounded-xl px-6 py-3"
            >
              Limpiar filtros
            </Button>
          </motion.div>
        )}

      </div>
    </div>
  );
};

export default PriceList;
